/*
 * TabBarRenderer — Canvas-Rendering und Hit-Testing der Tab-Leiste
 *
 * Rendert eine Zeichenzeile (y=0) mit:
 * - "Control" Button links
 * - Projekt-Tabs (aktiv/inaktiv) mit (x) Schließen-Button
 * - Scroll-Indikatoren wenn Tabs überlaufen
 *
 * Nutzt das bestehende Style-System und BoxChars.
 */

import { Style, BoxChars } from './config.js'

// Style-Index fuer Sync-Pending (Orange) — wird dynamisch aus config.js geladen
// falls vorhanden, sonst Fallback auf CONTROL_HOVER
const SYNC_PENDING_STYLE = Style.SYNC_PENDING ?? Style.CONTROL_HOVER

const CONTROL_LABEL = 'Control'
const CONTROL_WIDTH = CONTROL_LABEL.length + 3  // " Control ─"
const TAB_PADDING = 1      // Zeichen Abstand links/rechts im Tab-Titel
const CLOSE_BUTTON_WIDTH = 3  // "(x)"
const MIN_TAB_WIDTH = 8   // Minimale Tab-Breite (inkl. Rahmen)

export class TabBarRenderer {
    constructor() {
        // Layout-Daten (berechnet in _calculateLayout)
        this._tabPositions = []   // [{x, width, projectId, titleStart, titleEnd}]
        this._controlX = 0
        this._controlWidth = CONTROL_WIDTH
        this._scrollLeftX = -1    // -1 wenn nicht sichtbar
        this._scrollRightX = -1
        this._scrollOffset = 0   // Pixel-Offset für Tab-Scrolling
        this._totalTabsWidth = 0

        // Username-Bereich
        this._usernameX = -1  // Start-X des Username-Bereichs (-1 wenn nicht sichtbar)
        this._username = ''

        // Hover-State
        this._hoveredElement = null  // {type, projectId?, index?}
        this.isDirty = true
    }

    /**
     * Berechnet das Tab-Layout basierend auf Breite und offenen Tabs.
     *
     * @param {import('./TabManager.js').TabManager} tabManager
     * @param {Map<string, Object>} projectTitles - Map von projectId → {title}
     * @param {number} cols - Verfügbare Breite in Zeichen
     */
    _calculateLayout(tabManager, projectTitles, cols) {
        this._tabPositions = []

        const tabs = tabManager.openTabs
        if (tabs.length === 0) return

        // Tab-Breiten berechnen
        const tabWidths = tabs.map(id => {
            const title = projectTitles.get(id)?.title || 'untitled'
            // ┌─Title─(x)─┐ = 2 (Ränder) + 1 (Padding) + title + 1 (Padding) + 3 (x) + 1 (Padding)
            return Math.max(MIN_TAB_WIDTH, title.length + TAB_PADDING * 2 + CLOSE_BUTTON_WIDTH + 2)
        })

        this._totalTabsWidth = tabWidths.reduce((sum, w) => sum + w, 0)

        // Username-Bereich reservieren (Zeichen + 2 Leerzeichen + 1 Abstand)
        const usernameReserved = this._username ? this._username.length + 3 : 0

        // Verfügbarer Platz für Tabs (abzüglich Control-Bereich und Username)
        const availableWidth = cols - CONTROL_WIDTH - usernameReserved

        // Scroll-Indikatoren nötig?
        const needsScroll = this._totalTabsWidth > availableWidth
        const scrollIndicatorWidth = needsScroll ? 2 : 0  // "‹‹" / "››"
        const tabAreaWidth = availableWidth - scrollIndicatorWidth * 2
        const tabAreaStart = CONTROL_WIDTH + (needsScroll ? scrollIndicatorWidth : 0)
        const tabAreaEnd = tabAreaStart + tabAreaWidth

        // Scroll-Offset begrenzen
        const maxScrollOffset = Math.max(0, this._totalTabsWidth - tabAreaWidth)
        this._scrollOffset = Math.max(0, Math.min(this._scrollOffset, maxScrollOffset))

        // Scroll-Indikator-Positionen
        if (needsScroll) {
            this._scrollLeftX = this._scrollOffset > 0 ? CONTROL_WIDTH : -1
            this._scrollRightX = this._scrollOffset < maxScrollOffset ? tabAreaEnd : -1
        } else {
            this._scrollLeftX = -1
            this._scrollRightX = -1
        }

        // Tab-Positionen berechnen
        let x = tabAreaStart - this._scrollOffset
        for (let i = 0; i < tabs.length; i++) {
            const width = tabWidths[i]
            this._tabPositions.push({
                x,
                width,
                projectId: tabs[i],
                // Sichtbar wenn irgendein Teil im Tab-Bereich liegt
                visible: (x + width > tabAreaStart) && (x < tabAreaEnd)
            })
            x += width
        }
    }

    /**
     * Rendert die Tab-Leiste in Zeile y=0.
     *
     * @param {import('./renderer.js').Renderer} renderer
     * @param {import('./TabManager.js').TabManager} tabManager
     * @param {Map<string, Object>} projectTitles
     * @param {number} cols
     * @param {import('./persistence/SyncService.js').SyncService} [syncService]
     */
    render( renderer, tabManager, projectTitles, cols, syncService) {
        this._calculateLayout(tabManager, projectTitles, cols)

        const S = BoxChars.SINGLE
        const y = 0
        const cw = renderer.charWidth
        const ch = renderer.charHeight

        // Username-Bereich berechnen (reserviert rechten Rand)
        const usernameReserved = this._username ? this._username.length + 3 : 0
        const tabAreaEnd = cols - usernameReserved

        // 1. Tabs mit Canvas-Clipping rendern (begrenzt auf Tab-Bereich)
        const ctx = renderer.ctx
        ctx.save()
        ctx.beginPath()
        ctx.rect(CONTROL_WIDTH * cw, 0, (tabAreaEnd - CONTROL_WIDTH) * cw, ch)
        ctx.clip()

        for (let i = 0; i < this._tabPositions.length; i++) {
            const tab = this._tabPositions[i]
            if (!tab.visible) continue

            const isActive = tabManager.activeTab === tab.projectId
            const isHovered = this._hoveredElement?.type === 'tab' &&
                              this._hoveredElement?.projectId === tab.projectId
            const isCloseHovered = this._hoveredElement?.type === 'close' &&
                                   this._hoveredElement?.projectId === tab.projectId

            const borderStyle = isActive ? Style.PANE_BORDER_ACTIVE
                : isHovered ? Style.CONTROL_HOVER : Style.PANE_BORDER
            const titleStyle = isActive ? Style.PANE_TITLE_ACTIVE : Style.PANE_TITLE

            // Tab-Ränder
            let tx = tab.x
            renderer._renderCharAt(tx, y, S.TOP_LEFT, borderStyle)
            tx++

            // Titel (mit Breitenbegrenzung innerhalb des Tab-Rahmens)
            const title = projectTitles.get(tab.projectId)?.title || 'untitled'
            const maxTitleLen = tab.width - 2 - CLOSE_BUTTON_WIDTH - TAB_PADDING
            const displayTitle = title.length > maxTitleLen
                ? title.slice(0, maxTitleLen - 1) + '…' : title

            renderer._renderCharAt(tx, y, S.HORIZONTAL, borderStyle)
            tx++
            const maxLabelCols = tab.x + tab.width - CLOSE_BUTTON_WIDTH - 1 - tx
            const afterTitle = renderer.renderLabel(displayTitle, tx, y, titleStyle, isActive, maxLabelCols)
            tx = afterTitle

            // Auffüllen bis zum Close-Button
            while (tx < tab.x + tab.width - CLOSE_BUTTON_WIDTH - 1) {
                renderer._renderCharAt(tx, y, S.HORIZONTAL, borderStyle)
                tx++
            }

            // (x) Close-Button — orange wenn Cloud-Sync pending
            const hasPendingSync = syncService?.getPendingCount(tab.projectId) > 0
            const closeStyle = isCloseHovered ? Style.CONTROL_HOVER
                : hasPendingSync ? SYNC_PENDING_STYLE : borderStyle
            if (renderer.isProportional()) {
                renderer._renderControlIcon(tx + 1, y, 'x', closeStyle, borderStyle)
            } else {
                renderer._renderCharAt(tx, y, '(', borderStyle)
                renderer._renderCharAt(tx + 1, y, 'x', closeStyle)
                renderer._renderCharAt(tx + 2, y, ')', borderStyle)
            }
            tx += 3

            // Rechte Ecke
            renderer._renderCharAt(tx, y, S.TOP_RIGHT, borderStyle)
        }

        ctx.restore()

        // 2. "Control" Label links (über Tabs, nicht geclippt)
        const controlStyle = (this._hoveredElement?.type === 'control')
            ? Style.CONTROL_HOVER : Style.PANE_TITLE
        const afterControl = renderer.renderLabel(CONTROL_LABEL, 1, y, controlStyle)
        renderer._renderCharAt(afterControl, y, ' ', Style.PANE_BORDER)

        // 3. Scroll-Indikatoren (über Tabs — Hintergrund clearen, damit Tabs nicht durchscheinen)
        if (this._scrollLeftX >= 0) {
            ctx.clearRect(this._scrollLeftX * cw, 0, 2 * cw, ch)
            const scrollStyle = (this._hoveredElement?.type === 'scroll-left')
                ? Style.CONTROL_HOVER : Style.PANE_BORDER
            renderer._renderCharAt(this._scrollLeftX, y, '‹', scrollStyle)
            renderer._renderCharAt(this._scrollLeftX + 1, y, '‹', scrollStyle)
        }
        if (this._scrollRightX >= 0) {
            ctx.clearRect(this._scrollRightX * cw, 0, 2 * cw, ch)
            const scrollStyle = (this._hoveredElement?.type === 'scroll-right')
                ? Style.CONTROL_HOVER : Style.PANE_BORDER
            renderer._renderCharAt(this._scrollRightX, y, '›', scrollStyle)
            renderer._renderCharAt(this._scrollRightX + 1, y, '›', scrollStyle)
        }

        // 4. Username rechts angeschlagen (über Tabs, nicht geclippt)
        if (this._username) {
            const nameStyle = (this._hoveredElement?.type === 'username')
                ? Style.CONTROL_HOVER : Style.PANE_TITLE
            const nameGridW = renderer.labelGridWidth(this._username)
            const nameX = cols - nameGridW - 1
            this._usernameX = nameX
            renderer._renderCharAt(nameX - 1, y, ' ', Style.PANE_BORDER)
            renderer.renderLabel(this._username, nameX, y, nameStyle)
        }

        this.isDirty = false
    }

    /**
     * Hit-Testing: Was ist an Position charX in der Tab-Leiste?
     *
     * @param {number} charX
     * @param {number} cols
     * @param {number} [slop=0] - Horizontaler Slop (Char-Einheiten) fuer Touch.
     *   Exakter Treffer hat Vorrang; sonst ±1..±slop. Close-Button-Treffer aus
     *   dem Slop-Bereich werden VERWORFEN — Close ist destruktiv, da ist
     *   Pixel-Praezision notwendig (sonst loescht der User Tabs aus Versehen).
     * @returns {{type: string, projectId?: string}|null}
     */
    getElementAtPosition(charX, cols, slop = 0) {
        const exact = this._elementAtExact(charX, cols)
        if (exact || slop <= 0) return exact

        for (let dx = 1; dx <= slop; dx++) {
            for (const probe of [charX - dx, charX + dx]) {
                const hit = this._elementAtExact(probe, cols)
                if (!hit) continue
                if (hit.type === 'close') continue   // Close bleibt strikt
                return hit
            }
        }
        return null
    }

    /**
     * Interner pixelgenauer Hit-Test (alte getElementAtPosition-Logik).
     */
    _elementAtExact(charX, cols) {
        // Control-Bereich
        if (charX >= 0 && charX < CONTROL_WIDTH) {
            return { type: 'control' }
        }

        // Username-Bereich (rechts, falls vorhanden)
        if (this._usernameX >= 0 && charX >= this._usernameX && charX < (cols || Infinity)) {
            return { type: 'username' }
        }

        // Scroll links
        if (this._scrollLeftX >= 0 && charX >= this._scrollLeftX && charX < this._scrollLeftX + 2) {
            return { type: 'scroll-left' }
        }

        // Scroll rechts
        if (this._scrollRightX >= 0 && charX >= this._scrollRightX && charX < this._scrollRightX + 2) {
            return { type: 'scroll-right' }
        }

        // Tabs
        for (const tab of this._tabPositions) {
            if (!tab.visible) continue
            if (charX >= tab.x && charX < tab.x + tab.width) {
                // Close-Button? (letzte 4 Zeichen: "(x)┐")
                const closeStart = tab.x + tab.width - CLOSE_BUTTON_WIDTH - 1
                if (charX >= closeStart && charX < closeStart + CLOSE_BUTTON_WIDTH) {
                    return { type: 'close', projectId: tab.projectId }
                }
                return { type: 'tab', projectId: tab.projectId }
            }
        }

        return null
    }

    /**
     * Setzt den Hover-State und markiert als dirty falls geändert.
     * @param {number} charX
     * @param {number} [cols]
     */
    setHover(charX, cols) {
        const element = this.getElementAtPosition(charX, cols)
        const prev = this._hoveredElement

        // Vergleich: Hat sich was geändert?
        const changed = !prev && element ||
                        prev && !element ||
                        prev?.type !== element?.type ||
                        prev?.projectId !== element?.projectId

        if (changed) {
            this._hoveredElement = element
            this.isDirty = true
        }
    }

    /**
     * Entfernt den Hover-State.
     */
    clearHover() {
        if (this._hoveredElement) {
            this._hoveredElement = null
            this.isDirty = true
        }
    }

    /**
     * Scrollt die Tab-Leiste nach links.
     */
    scrollLeft() {
        if (this._scrollOffset > 0) {
            this._scrollOffset = Math.max(0, this._scrollOffset - 10)
            this.isDirty = true
        }
    }

    /**
     * Scrollt die Tab-Leiste nach rechts.
     */
    scrollRight() {
        this._scrollOffset += 10
        this.isDirty = true
    }

    /**
     * Scrollt zum aktiven Tab (damit er sichtbar ist).
     * @param {import('./TabManager.js').TabManager} tabManager
     */
    ensureActiveTabVisible(tabManager) {
        const activeTab = this._tabPositions.find(t => t.projectId === tabManager.activeTab)
        if (!activeTab || activeTab.visible) return

        // Zum aktiven Tab scrollen
        const tabAreaStart = CONTROL_WIDTH + (this._scrollLeftX >= 0 ? 2 : 0)
        if (activeTab.x < tabAreaStart) {
            this._scrollOffset -= (tabAreaStart - activeTab.x)
        } else {
            this._scrollOffset += (activeTab.x + activeTab.width - tabAreaStart)
        }
        this._scrollOffset = Math.max(0, this._scrollOffset)
        this.isDirty = true
    }
}
